package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportMapping
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportOrder
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportPreview
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraImportResult
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraOrder
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraReason
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnit
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnitSuggestion
import br.com.vvrefeicoes.portal.domain.repository.PurchasesExtraOrderRepository
import kotlinx.coroutines.CancellationException

class PurchasesExtraOrdersController(
    private val repository: PurchasesExtraOrderRepository,
) {
    var orders by mutableStateOf<List<PurchasesExtraOrder>>(emptyList())
        private set

    var count by mutableStateOf(0L)
        private set

    var totalItems by mutableStateOf(0L)
        private set

    var loading by mutableStateOf(false)
        private set

    var creating by mutableStateOf(false)
        private set

    var error by mutableStateOf<String?>(null)
        private set

    var units by mutableStateOf<List<PurchasesExtraUnit>>(emptyList())
        private set

    var reasons by mutableStateOf<List<PurchasesExtraReason>>(emptyList())
        private set

    var reasonsLoading by mutableStateOf(false)
        private set

    var reasonError by mutableStateOf<String?>(null)
        private set

    var importPreview by mutableStateOf<PurchasesExtraImportPreview?>(null)
        private set

    var importError by mutableStateOf<String?>(null)
        private set

    var importing by mutableStateOf(false)
        private set

    suspend fun load(period: MonthPeriod) {
        loading = true
        try {
            orders = repository.load(period)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            orders = emptyList()
            error = exception.message ?: "Não foi possível carregar os pedidos extras."
        } finally {
            loading = false
        }
    }

    fun clearCachedData() {
        orders = emptyList()
        count = 0L
        totalItems = 0L
        units = emptyList()
        loading = false
    }

    suspend fun loadSummary(period: MonthPeriod) {
        try {
            count = repository.count(period)
            totalItems = count
        } catch (exception: CancellationException) {
            throw exception
        } catch (_: Throwable) {
            count = 0L
            totalItems = 0L
        }
    }

    suspend fun loadUnits(): List<PurchasesExtraUnit> =
        try {
            units = repository.units()
            units
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível carregar as unidades."
            emptyList()
        }

    suspend fun loadReasons(includeInactive: Boolean = true) {
        reasonsLoading = true
        try {
            reasons = repository.reasons(includeInactive)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            reasonError = exception.message ?: "Não foi possível carregar os motivos."
        } finally {
            reasonsLoading = false
        }
    }

    suspend fun createReason(name: String): Boolean =
        reasonAction {
            repository.createReason(name)
        }

    suspend fun renameReason(
        id: String,
        name: String,
    ): Boolean =
        reasonAction {
            repository.renameReason(id, name)
        }

    suspend fun changeReasonStatus(
        id: String,
        active: Boolean,
    ): Boolean =
        reasonAction {
            repository.changeReasonStatus(id, active)
        }

    private suspend fun reasonAction(action: suspend () -> PurchasesExtraReason): Boolean {
        reasonError = null
        return try {
            val updated = action()
            reasons =
                (reasons.filterNot { it.id == updated.id } + updated)
                    .sortedWith(compareByDescending<PurchasesExtraReason> { it.active }.thenBy { it.name })
            true
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            reasonError = exception.message ?: "Não foi possível salvar o motivo."
            false
        }
    }

    fun dismissReasonError() {
        reasonError = null
    }

    suspend fun previewUnits(labels: List<String>): List<PurchasesExtraUnitSuggestion> =
        try {
            repository.previewUnits(labels)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            importError = exception.message ?: "Não foi possível carregar as unidades."
            emptyList()
        }

    fun resetImport() {
        importPreview = null
        importError = null
        importing = false
    }

    fun dismissError() {
        error = null
    }

    fun dismissImportError() {
        importError = null
    }

    suspend fun previewImport(
        period: MonthPeriod,
        orders: List<PurchasesExtraImportOrder>,
    ): PurchasesExtraImportPreview? {
        importPreview = null
        importError = null
        return try {
            repository.previewImport(period, orders).also { importPreview = it }
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            importError = exception.message ?: "Não foi possível validar a planilha."
            null
        }
    }

    suspend fun importOrders(
        period: MonthPeriod,
        orders: List<PurchasesExtraImportOrder>,
        mappings: List<PurchasesExtraImportMapping>,
    ): PurchasesExtraImportResult? {
        importing = true
        importError = null
        return try {
            repository.importOrders(period, orders, mappings)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            importError = exception.message ?: "Não foi possível importar os pedidos extras."
            null
        } finally {
            importing = false
        }
    }

    suspend fun create(
        period: MonthPeriod,
        order: PurchasesExtraOrder,
    ): PurchasesExtraOrder? {
        creating = true
        error = null
        return try {
            repository.create(period, order)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível adicionar o pedido extra."
            null
        } finally {
            creating = false
        }
    }

    suspend fun update(order: PurchasesExtraOrder): PurchasesExtraOrder? =
        try {
            repository.update(order)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível salvar o pedido extra."
            null
        }

    suspend fun delete(order: PurchasesExtraOrder): Boolean =
        try {
            error = null
            repository.delete(order.id)
            true
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível excluir o pedido extra."
            false
        }
}
